import { datadogLogs } from '@datadog/browser-logs';
import { isNull, isString, isUndefined, pickBy } from 'lodash';
import { useContext, useEffect, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';

import getEnvironment from '../../config/environment';
import { track, trackTiming } from '../../config/mixpanel';
import { logMixpanelDebug } from '../../config/mixpanelDebugMode';
import { getPortalRuntimeConfig } from '../../services/runtimeConfig';
import combineTrackingData from './combine-tracking-data';
import { mixpanelContext } from './MixpanelProvider';
import useGeneralData from './useGeneralData';

export enum componentType {
  header = 'header',
  patients = 'patients',
  allPatients = 'all_patients',
  patientSidebar = 'patient_sidebar',
  home = 'home',
  homepage = 'homepage',
  appInvitation = 'app_invitation',
  report = 'report',
  patientOverview = 'patient_overview',
  weeklyStatus = 'weekly_status',
  fallRiskWidget = 'fall_risk_widget',
  fallRiskV3Widget = 'fall_risk_v3_widget',
  walkScoreWidget = 'walk_score_widget',
  registrationFlow = 'registration_flow',
  recommendationWidget = 'recommendation_widget',
}

export interface IUseMixpanelProps {
  initData?: any; // eslint-disable-line
  screen?: string;
  patientUuid?: string;
}

export interface ITrackProps {
  action: string;
  data?: any; // eslint-disable-line
}

export interface IClickTrackProps extends ITrackProps {
  component?: componentType | string;
}

export interface IUseMixpanel {
  track(text: string, data?: any): void; // eslint-disable-line
  trackFail(text: string, data?: any, logDataDog?: boolean): void; // eslint-disable-line
  trackSuccess(text: string, data?: any): void; // eslint-disable-line
  trackClick(props: IClickTrackProps | string): void;
  generalData: ReturnType<typeof useGeneralData>;
  props: IUseMixpanelProps;
}

const IDLE_TIMEOUT = 2 * 60 * 1000;

export default function useMixpanel(props: IUseMixpanelProps = {}): IUseMixpanel {
  const generalData = useGeneralData();

  const context = useContext<IUseMixpanelProps | null>(mixpanelContext);

  const trackingData = combineTrackingData(props, context);
  const idles = useRef(0);
  const activeTimer = useIdleTimer({
    timeout: IDLE_TIMEOUT,
    onIdle: () => {
      idles.current++;
    },
  });

  const trackToMixpanel = (props: ITrackProps) => {
    if (!isString(props.action)) {
      if (!getEnvironment().isProd) {
        console.warn('illegal mixpanel track');
        console.warn(props);
      } else {
        datadogLogs.logger?.warn?.('illegal mixpanel track', props);
      }
    }

    const allTrackingValues = pickBy(
      {
        screen: trackingData.screen,
        patientUuid: trackingData.patientUuid,
        version: getPortalRuntimeConfig().appVersion,
        ...trackingData?.initData,
        ...generalData,
        ...props.data,
      },
      (prop) => !isUndefined(prop) && !isNull(prop),
    );

    track(props.action, allTrackingValues);
    logMixpanelDebug(props.action, allTrackingValues);
  };

  useEffect(() => {
    const action = `screen: ${props.screen}`;
    const finishAction = `finish_${action}`;
    if (props?.screen) {
      idles.current = 0;
      activeTimer.reset();
      trackToMixpanel({
        action,
      });

      trackTiming(finishAction);
    }

    return () => {
      if (props.screen) {
        const activeTime =
          idles.current * IDLE_TIMEOUT +
          (IDLE_TIMEOUT - activeTimer.getRemainingTime()) +
          activeTimer.getTotalActiveTime();

        trackToMixpanel({
          action: finishAction,
          data: {
            activeTimeSeconds: activeTimer.getTotalActiveTime() / 1000,
            activeTimeSecondsCalc: activeTime / 1000,
          },
        });
      }
    };
  }, [props?.screen]);

  return {
    track(text: string, data?: any) {
      // eslint-disable-line
      trackToMixpanel({
        action: text,
        data,
      });
    },
    trackFail(text: string, data?: any, logDataDog = true) {
      // eslint-disable-line
      trackToMixpanel({
        action: `failed: ${text}`,
        data,
      });
      if (logDataDog) {
        datadogLogs.logger?.error(`failed: ${text}`, data);
      }
    },
    trackSuccess(text, data?: any) {
      // eslint-disable-line
      trackToMixpanel({
        action: `success: ${text}`,
        data,
      });
    },
    trackClick(props: IClickTrackProps | string) {
      const data =
        typeof props === 'string'
          ? {}
          : {
              component: props.component,
              ...props?.data,
            };
      const action = `clicked: ${typeof props === 'string' ? props : props.action}`;

      trackToMixpanel({
        data,
        action,
      });
    },
    generalData,
    props: trackingData,
  };
}
